import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { downloadTextFile, safeFilename } from '../../../lib/download';
import { seatingChartKey, seatingChartsKey } from '../../../lib/groupQueryKeys';
import {
  chartPickerLabel, chartStatusLabel, seatingChartCsv, seatingExportByTable,
} from '../../../lib/hostExport/seatingExport';
import { seatingApi } from '../../../services/seating-api-client';
import type { GroupDetail } from '../../../types/groups';
import type { SeatingChartSummary } from '../../../types/seating';
import PrintableSection from './PrintableSection';
import QueryState from './QueryState';

/**
 * Every table, every chair, who is in it — one chart at a time (a trip can
 * hold several; a chart picker appears only once there is a choice to make).
 * Chair contents come from `seatingExportByTable` (lib/hostExport/seatingExport.ts),
 * which reads through the same `seatMapFor`/`occupantName` the seating editor
 * itself uses.
 *
 * Both queries use the Seating tab's own keys, so a chart the host just
 * arranged is served from cache here — and a seat moved there invalidates
 * this copy too.
 */
export default function SeatingExportSection({
  group,
  printing,
  onPrint,
}: {
  group: GroupDetail;
  printing: boolean;
  onPrint: () => void;
}) {
  const [selectedChartId, setSelectedChartId] = useState('');

  const chartsQuery = useQuery<SeatingChartSummary[]>(
    seatingChartsKey(group.id),
    () => seatingApi.listCharts(group.id),
    { staleTime: 30_000 },
  );
  const charts = chartsQuery.data ?? [];
  // Nothing picked yet defaults to the first chart, but a host who did pick
  // one keeps it even if the list re-sorts underneath them.
  const activeChartId = selectedChartId || charts[0]?.id || '';
  const activeChart = charts.find((chart) => chart.id === activeChartId);

  const chartQuery = useQuery(
    seatingChartKey(group.id, activeChartId),
    () => seatingApi.getChart(group.id, activeChartId),
    { enabled: Boolean(activeChartId) },
  );

  const chart = chartQuery.data;
  const tables = useMemo(() => (chart ? seatingExportByTable(chart) : []), [chart]);
  const ready = Boolean(chart);

  return (
    <PrintableSection
      title="Seating chart"
      description="Every table, every chair, who is in it."
      printing={printing}
      onPrint={ready ? onPrint : undefined}
      onDownloadCsv={
        chart
          ? () => downloadTextFile(
              `seating-chart-${safeFilename(activeChart?.name, group.slug)}.csv`,
              seatingChartCsv(chart),
            )
          : undefined
      }
    >
      {charts.length > 1 ? (
        <TextField
          select
          size="small"
          label="Chart"
          value={activeChartId}
          onChange={(event) => setSelectedChartId(event.target.value)}
          sx={{ mb: 2, minWidth: 280, '@media print': { display: 'none' } }}
        >
          {charts.map((option) => (
            <MenuItem key={option.id} value={option.id}>{chartPickerLabel(option)}</MenuItem>
          ))}
        </TextField>
      ) : null}
      {activeChart ? (
        // On paper too: a host holding a printed draft should be able to see
        // that it is a draft.
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {activeChart.name} · {chartStatusLabel(activeChart)}
        </Typography>
      ) : null}
      {!chartsQuery.isLoading && charts.length === 0 ? (
        <Typography color="text.secondary">No seating charts yet.</Typography>
      ) : null}
      <QueryState
        loading={chartsQuery.isLoading || chartQuery.isLoading}
        loadingLabel="Loading seating chart…"
        error={chartsQuery.error || chartQuery.error ? "Couldn't load the seating chart." : ''}
      />
      {tables.map((table) => (
        <Box key={table.table} sx={{ mb: 3, breakInside: 'avoid' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {table.table} ({table.filled} of {table.capacity} seated)
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell component="th" scope="col">Seat</TableCell>
                <TableCell component="th" scope="col">Occupant</TableCell>
                <TableCell component="th" scope="col">Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row) => (
                <TableRow key={row.seatNumber}>
                  <TableCell component="th" scope="row">{row.seatNumber}</TableCell>
                  <TableCell>{row.occupant}</TableCell>
                  <TableCell>{row.note}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ))}
    </PrintableSection>
  );
}
